import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { SettingType } from '@/types/app';
import SourceIcon from './SourceIcon';

interface SummaryCardProps {
	type: SettingType;
	title: string;
	children: ReactNode;
	onClick: (type: SettingType) => void;
	isActive: boolean;
	source: string | null;
	loading: boolean;
	idx?: number;
	isConfirmed?: boolean;
}

const SummaryCard = ({
	type,
	title,
	children,
	onClick,
	isActive,
	source,
	loading,
	idx,
	isConfirmed = false
}: SummaryCardProps) => {
	return (
		<div
			data-settings={type}
			data-testid={`settings-summary-card-${type}`}
			className={cn(
				'cursor-pointer group w-full',
				type === 'employees' && 'sm:w-[40%]',
				type === 'absence' && 'sm:w-[25%]',
				type === 'coverage' && 'sm:w-[35%]',
				loading && 'saturate-[0.75]',
				idx !== 0 && 'sm:border-l border-t sm:border-t-0 border-border'
			)}
			onClick={() => onClick(type)}
			onKeyDown={(e) => {
				if (isActive) return;
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onClick(type);
				}
			}}
			tabIndex={0}
			role="button"
			aria-label={`Select ${title} settings`}
		>
			<div className="flex flex-col h-full">
				{/*
				 * Mobile: cards stack, so lay each one out as a row — label left,
				 * value right-aligned — for a compact, scannable summary (#494).
				 * sm+: cards sit side by side, so revert to label-above-value.
				 */}
				<div className="grow p-4 sm:pr-1 sm:mt-1 flex items-start justify-between gap-2 sm:block sm:items-center">
					<div className="inline-flex items-center whitespace-nowrap shrink-0 text-muted-foreground text-base sm:pb-2 sm:text-sm">
						<span className="font-bold">{title}</span>
						<SourceIcon source={source} isConfirmed={isConfirmed} />
					</div>
					<div className="min-w-0 text-foreground text-right sm:text-left">{children}</div>
				</div>
				<div
					data-setting-selected-indicator
					className={cn(
						'bg-primary w-full p-1 mt-auto transition-all duration-200',
						!isActive && 'opacity-40 group-hover:opacity-100'
					)}
				/>
			</div>
		</div>
	);
};

export default SummaryCard;
